import React, { useState } from "react";
import { api } from "../../lib/api";
import { apiErrorCode } from "../../lib/apiErrors";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { currencyLabel } from "../../lib/currencies";
import { formatMoneyCode } from "../../lib/formatMoney";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import RefundTransactionPicker from "./RefundTransactionPicker";
import { financeErrorText, RETRY_BLOCKING_CODES, RELOAD_CODES, STALE_REFUND_CODES } from "./financeErrorText";
import { instantDay } from "../../lib/calendarDay";

// A positive amount with at most two decimals, as typed ("1e3", "-5", "10.005" and "" are not).
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const parseAmount = (raw) => {
  const s = String(raw).trim();
  if (!AMOUNT_PATTERN.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
};
const isBlank = (raw) => String(raw).trim() === "";
const toCents = (major) => Math.round(major * 100);

const FIELD_CLASS = "bg-white border-slate-200";
const LABEL_CLASS = "text-xs text-slate-500 mb-1 block";

/**
 * Rebate, custom price and refund — the three Tenant 360 money writes.
 *
 * The server owns the currency: rebate and custom price are denominated in the practice's
 * billing currency (shown read-only), a refund in the chosen payment's own. Request bodies are
 * exactly `{amount, reason}`, `{amount, reason, effective_until}` and
 * `{transaction_id, amount, reason}` (S7 contract section 2, D4 contract section 3.1).
 *
 * The custom price is ONE price, in the cycle the practice is billed in (`billing`, `/360.billing`):
 * the server re-prices the Stripe subscription from the next invoice (`nextInvoiceAt`, no
 * proration). A practice billed nothing (`billing` null) has no subscription to re-price: the modal
 * says so and cannot send.
 *
 * A write that did not take effect is shown inline and the modal stays open: the localized
 * sentence of the refusal code (financeErrorText.js), never a success toast. After a refusal
 * that retrying cannot fix, or that could double-apply money, Confirm stays disabled.
 *
 * `onSettled()`: any answer, success or refusal (a refused attempt may have written a history row).
 * `onDone()`: the action took effect; the page closes the modal and re-reads the practice.
 * `onStale()`: a refusal that says the page is out of date (RELOAD_CODES); the modal stays open
 * with the sentence while the page re-reads the practice.
 */
export default function FinanceActionModal({
  type, tenantId, billingCurrency, billing = null, nextInvoiceAt = null, onClose, onSettled, onDone, onStale,
}) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [form, setForm] = useState({ amount: "", reason: "", until: "" });
  const [selected, setSelected] = useState(null); // refund: the chosen row of refundable-transactions
  const [stage, setStage] = useState("form"); // "form" | "review" (refund only)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  // Bumped when the refundable list on screen is known to be stale: the picker remounts and re-reads.
  const [pickerGeneration, setPickerGeneration] = useState(0);

  const setField = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const currency = type === "refund" ? currencyLabel(selected?.amount?.currency) : currencyLabel(billingCurrency);
  const remaining = selected?.refundable;
  const remainingText = remaining ? formatMoneyCode(remaining.cents, remaining.currency) : "";
  const rowDate = selected ? instantDay(selected.created_at, lang) || "—" : "";

  // The request this modal would send, or `null` after putting the reason it cannot in `error`.
  const buildRequest = () => {
    if (form.reason.trim().length < 3) { setError(t("t360.reasonRequired")); return null; }
    const base = `/super-admin/tenants/${tenantId}/billing`;
    const reason = form.reason.trim();
    if (type === "rebate") {
      const amount = parseAmount(form.amount);
      if (amount === null) { setError(t("t360f.amountInvalid")); return null; }
      return { path: `${base}/rebate`, body: { amount, reason } };
    }
    if (type === "adjust") {
      const amount = parseAmount(form.amount);
      if (amount === null) { setError(t("t360f.amountInvalid")); return null; }
      return { path: `${base}/adjust-price`, body: { amount, reason, effective_until: isBlank(form.until) ? null : form.until } };
    }
    if (!selected) { setError(t("t360f.refund.selectFirst")); return null; }
    const typed = isBlank(form.amount) ? null : parseAmount(form.amount);
    if (!isBlank(form.amount) && typed === null) { setError(t("t360f.amountInvalid")); return null; }
    if (typed !== null && toCents(typed) > remaining.cents) {
      setError(t("t360f.refund.exceeds", { amount: remainingText }));
      return null;
    }
    // A blank amount is the full remaining balance AS REVIEWED, sent as that figure: `null` would
    // let the server refund whatever remains at Confirm time, a different amount from the one
    // the operator confirmed if another refund landed in between (that case is now a 409).
    const amount = typed === null ? remaining.cents / 100 : typed;
    return { path: `${base}/refund`, body: { transaction_id: selected.refund_ref, amount, reason } };
  };

  const successText = (answer) => {
    const status = answer?.status;
    if (type === "rebate") return t(status === "issued_demo" ? "t360f.rebateRecordedDemo" : "t360f.rebateApplied");
    if (type === "refund") return t(status === "succeeded_demo" ? "t360f.refundRecordedDemo" : "t360f.refundIssued");
    if (status === "applied_demo") return t("t360f.customPrice.appliedDemo");
    const date = instantDay(answer?.custom_price?.bills_from, lang);
    return date ? t("t360f.customPrice.applied", { date }) : t("t360f.customPrice.appliedNoDate");
  };

  const send = async (request) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(request.path, request.body);
      toast.success(successText(r.data));
      onSettled();
      onDone();
    } catch (err) {
      const code = apiErrorCode(err);
      const stale = type === "refund" && STALE_REFUND_CODES.includes(code);
      const text = financeErrorText(err, { t, fallback: t("t360.error") });
      // The code's sentence says what the server found; a stale choice adds what this modal
      // did about it, so the operator knows the list below is current.
      setError(stale ? `${text} ${t("t360f.refund.listRefreshed")}` : text);
      if (RETRY_BLOCKING_CODES.includes(code)) setBlocked(true);
      if (type === "adjust" && RELOAD_CODES.includes(code)) onStale();
      if (stale) {
        // The chosen row no longer says what the server holds: drop it, go back to the list and
        // re-read it, so the operator chooses again from current balances. The reason is kept.
        setSelected(null);
        setForm((f) => ({ ...f, amount: "" }));
        setStage("form");
        setPickerGeneration((n) => n + 1);
      }
      onSettled();
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const request = buildRequest();
    if (!request) return;
    if (type === "refund") { setError(null); setStage("review"); return; }
    send(request);
  };
  const confirmRefund = () => {
    const request = buildRequest();
    if (request) send(request);
  };

  const TITLES = { rebate: t("t360.titleRebate"), adjust: t("t360.titleAdjust"), refund: t("t360.titleRefund") };
  const reviewing = type === "refund" && stage === "review";
  // A custom price needs a subscription to re-price (`billing`) and an amount.
  const submitDisabled = busy || blocked || (type === "adjust" && (!billing || isBlank(form.amount)));
  const billsFromDate = instantDay(nextInvoiceAt, lang);
  const reviewAmount = isBlank(form.amount) ? null : parseAmount(form.amount);

  const currencyRow = (
    <div className="mb-3">
      <span className={LABEL_CLASS}>{t("t360f.currency")}</span>
      <div className="text-sm text-slate-800 font-medium" data-testid="modal-currency" title={t("t360f.currencyFixed")}>
        {currency || "—"}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit} noValidate onClick={(e) => e.stopPropagation()} data-testid={`action-modal-${type}`}
        className={`bg-white border border-slate-200 rounded-2xl p-6 w-full max-h-[90vh] overflow-y-auto ${type === "refund" ? "max-w-3xl" : "max-w-md"}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sol font-semibold text-xl">{TITLES[type]}</h3>
          <button type="button" onClick={onClose} data-testid="modal-close"><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className={reviewing ? "hidden" : ""}>
          {type === "refund" && (
            <RefundTransactionPicker
              key={pickerGeneration} tenantId={tenantId} selectedRef={selected?.refund_ref ?? null}
              onSelect={(row) => { setSelected(row); setForm((f) => ({ ...f, amount: "" })); setError(null); }}
            />
          )}

          {(type !== "refund" || selected) && (
            <>
              {type === "adjust" ? (
                <>
                  {!billing && (
                    <p data-testid="modal-no-billing" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      {t("t360f.error.NO_BILLING_SUBSCRIPTION")}
                    </p>
                  )}
                  <div className="mb-3">
                    <label className={LABEL_CLASS} data-testid="modal-price-label">
                      {t(`t360f.adjust.price.${billing?.cycle === "annual" ? "annual" : "monthly"}`)}
                    </label>
                    <Input type="number" step="0.01" value={form.amount} onChange={setField("amount")}
                      className={FIELD_CLASS} data-testid="modal-price" />
                  </div>
                  <div className="mb-3">
                    <label className={LABEL_CLASS} data-testid="modal-until-label">{t("t360f.adjust.until")}</label>
                    <Input type="date" value={form.until} onChange={setField("until")}
                      className={FIELD_CLASS} data-testid="modal-until" />
                  </div>
                  {billing && (
                    <p className="text-xs text-slate-500 mb-3" data-testid="modal-bills-from">
                      {billsFromDate ? t("t360f.adjust.billsFrom", { date: billsFromDate }) : t("t360f.adjust.billsFromUnknown")}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mb-3" data-testid="modal-owner-notified">{t("t360f.adjust.ownerNotified")}</p>
                </>
              ) : (
                <div className="mb-3">
                  <label className={LABEL_CLASS}>{t("t360.amount")} {type === "refund" && t("t360.amountRefundHint")}</label>
                  <Input type="number" step="0.01" value={form.amount} onChange={setField("amount")}
                    placeholder={type === "refund" ? t("t360f.refund.amountPlaceholder", { amount: remainingText }) : undefined}
                    className={FIELD_CLASS} data-testid="modal-amount" />
                </div>
              )}

              {currencyRow}

              <div className="mb-4">
                <label className={LABEL_CLASS}>{t("t360.justification")}</label>
                <textarea value={form.reason} onChange={setField("reason")} data-testid="modal-reason" rows={3}
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </div>
            </>
          )}
        </div>

        {reviewing && (
          <div className="mb-4" data-testid="refund-review">
            <h4 className="font-semibold text-sm mb-3">{t("t360f.refund.reviewTitle")}</h4>
            <dl className="text-sm space-y-2">
              <div>
                <dt className="text-xs text-slate-500">{t("t360f.refund.reviewPayment")}</dt>
                <dd>{rowDate} · {selected.plan_name || selected.description || t("t360f.refund.noDescription")}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t("t360f.refund.reviewReference")}</dt>
                <dd><code>{selected.refund_ref}</code></dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t("t360f.refund.reviewAmount")}</dt>
                <dd>
                  {formatMoneyCode(reviewAmount === null ? remaining.cents : toCents(reviewAmount), remaining.currency)}
                  {reviewAmount === null && <span className="text-slate-400"> · {t("t360f.refund.reviewFull")}</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t("t360f.currency")}</dt>
                <dd>{currency}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{t("t360f.refund.reviewReason")}</dt>
                <dd className="whitespace-pre-wrap break-words">{form.reason.trim()}</dd>
              </div>
            </dl>
          </div>
        )}

        {error && (
          <p role="alert" data-testid="modal-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 mb-4">
            {error}
          </p>
        )}

        {reviewing ? (
          <div className="flex gap-3">
            <Button type="button" variant="outline" disabled={busy} onClick={() => { setError(null); setStage("form"); }}
              className="flex-1" data-testid="refund-back">
              {t("t360f.refund.back")}
            </Button>
            <Button type="button" disabled={submitDisabled} onClick={confirmRefund}
              className="flex-1 bg-amber-300 text-black hover:bg-amber-200" data-testid="refund-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("t360f.refund.confirm")}
            </Button>
          </div>
        ) : (
          (type !== "refund" || selected) && (
            <Button type="submit" disabled={submitDisabled} className="w-full bg-amber-300 text-black hover:bg-amber-200" data-testid="modal-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                : t(type === "refund" ? "t360f.refund.review" : "t360.confirm")}
            </Button>
          )
        )}
      </form>
    </div>
  );
}
